import React, { createContext, useContext, useState, useEffect } from "react";

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      "useLocalization must be used within a LocalizationProvider"
    );
  }
  return context;
};

// Translation files
const translations = {
  en: {
    // Common
    question: "Question",
    outOf: "out of",
    years: "years",
    next: "Next",
    previous: "Previous",
    complete: "Complete",
    submitting: "Submitting...",
    male: "Male",
    female: "Female",
    welcome: "Welcome",
    loading: "Loading...",
    error: "Error",
    retry: "Retry",
    back: "Back",
    submit: "Submit",
    cancel: "Cancel",
    confirm: "Confirm",
    close: "Close",
    loadingAssessments: "Loading assessments...",

    // Dashboard
    dashboard: {
      welcomeMessage: "Welcome, {name}!",
      checkVitals: "Check Your Vitals",
      joinConsultation: "Join Online Consultation",
      healthAssessment: "Health Risk Assessment",
      chatManager: "Chat with Case Manager",
      knowledgeBank: "PHM Knowledge Bank",
      endSession: "End session",
    },

    // Welcome Screen
    welcomeScreen: {
      title: "Welcome to the Future of Wellbeing",
      subtitle: "Your health companion",
      getStarted: "Start Your Journey",
    },

    // Vitals Check
    vitalsCheck: {
      title: "Vitals Check",
      instructions: "Please follow the instructions to check your vitals",
      startCheck: "Start Vitals Check",
      proceedToAssessments: "Proceed to assessments",
      vitals: {
        muscleMass: "Muscle Mass",
        weight: "Weight",
        boneMass: "Bone Mass",
        heartPulse: "Heart Pulse",
        fatPercentage: "Fat Percentage",
        bloodPressure: "Blood Pressure",
        bmi: "BMI",
        metabolicAge: "Metabolic Age",
        nerveHealth: "Nerve Health",
        fatRatio: "Fat Ratio",
        hydration: "Hydration",
      },
    },

    // Consultation
    consultation: {
      title: "Online Consultation",
      joinCall: "Join Call",
      waitingRoom: "Waiting Room",
      preparing: "Preparing your consultation...",
      connectionError:
        "Unable to connect. Please try again or contact support.",
      retry: "Retry",
      endMeeting: "End Meeting",
      endingMeeting: "Ending meeting...",
    },

    // Meeting SDK
    meetingSDK: {
      callError: "Call Error",
      joiningCall: "Joining call...",
      retry: "Retry",
      errorOccurred: "An error occurred during the call",
      failedToJoin: "Failed to join the call",
      connectingToDoctor: "We're connecting you to your doctor — your consultation will begin shortly.",
    },

    // Assessment
    assessment: {
      title: "Health Assessment",
      description: "Your answers help your doctor tailor your consultation",
      skipAll: "Skip all",
      talkToDoctor: "Talk to a doctor",
      questions: "questions",
    },

    // End Session
    endSession: {
      title: "You've successfully completed your pod session",
      description:
        "Your measurements report and medical profile link have been sent to your email.",
      endButton: "End Session",
      confirmEnd: "Are you sure you want to end this session?",
      sessionSummary: "Session Summary",
    },

    // Chat Manager
    chatManager: {
      title: "Chat with Case Manager",
      instruction:
        "Scan the QR code and we'll connect you with one of our case managers",
    },

    // Language Toggle
    language: {
      english: "English",
      arabic: "العربية",
      selectLanguage: "Select Language",
    },
  },

  ar: {
    // Common
    question: "سؤال",
    outOf: "من",
    years: "سنة",
    complete: "إنهاء",
    submitting: "جاري التسليم...",
    male: "ذكر",
    female: "أنثى",
    welcome: "مرحباً",
    loading: "جاري التحميل...",
    error: "خطأ",
    retry: "إعادة المحاولة",
    back: "رجوع",
    next: "التالي",
    previous: "السابق",
    submit: "إرسال",
    cancel: "إلغاء",
    confirm: "تأكيد",
    close: "إغلاق",
    loadingAssessments: "جاري تحميل التقييمات...",
    // Dashboard
    dashboard: {
      welcomeMessage: "مرحباً، {name}!",
      checkVitals: "فحص العلامات الحيوية",
      joinConsultation: "انضم للاستشارة عبر الإنترنت",
      healthAssessment: "تقييم المخاطر الصحية",
      chatManager: "تحدث مع مدير الحالة",
      knowledgeBank: "بنك المعرفة الصحي",
      endSession: "إنهاء الجلسة",
    },

    // Welcome Screen
    welcomeScreen: {
      title: "مرحباً بك في مستقبل الرفاهية",
      subtitle: "رفيقك الصحي",
      getStarted: "ابدأ رحلتك",
    },

    // Vitals Check
    vitalsCheck: {
      title: "فحص العلامات الحيوية",
      instructions: "يرجى اتباع التعليمات لفحص العلامات الحيوية",
      startCheck: "بدء فحص العلامات الحيوية",
      proceedToAssessments: "المتابعة إلى التقييمات",
      vitals: {
        muscleMass: "كتلة العضلات",
        weight: "الوزن",
        boneMass: "كتلة العظام",
        heartPulse: "نبض القلب",
        fatPercentage: "نسبة الدهون",
        bloodPressure: "ضغط الدم",
        bmi: "مؤشر كتلة الجسم",
        metabolicAge: "العمر الأيضي",
        nerveHealth: "صحة الأعصاب",
        fatRatio: "نسبة الدهون",
        hydration: "الترطيب",
      },
    },

    // Consultation
    consultation: {
      title: "الاستشارة عبر الإنترنت",
      joinCall: "انضم للمكالمة",
      waitingRoom: "غرفة الانتظار",
      preparing: "جاري تحضير استشارتك...",
      connectionError:
        "غير قادر على الاتصال. يرجى المحاولة مرة أخرى أو الاتصال بالدعم.",
      retry: "إعادة المحاولة",
      endMeeting: "إنهاء المكالمة",
      endingMeeting: "جاري إنهاء المكالمة...",
    },

    // Meeting SDK
    meetingSDK: {
      callError: "خطأ في المكالمة",
      joiningCall: "جاري الانضمام للمكالمة...",
      retry: "إعادة المحاولة",
      errorOccurred: "حدث خطأ أثناء المكالمة",
      failedToJoin: "فشل في الانضمام للمكالمة",
      connectingToDoctor: "نحن نوصلك بطبيبك — ستبدأ استشارتك قريباً.",
    },

    // Assessment
    assessment: {
      title: "التقييم الصحي",
      description: "تساعد إجاباتك على تخصيص استشارتك للطبيب",
      skipAll: "تخطي الكل",
      talkToDoctor: "تحدث مع الطبيب",
      questions: "أسئلة",
    },

    // End Session
    endSession: {
      title: "لقد أكملت جلسة البود بنجاح",
      description:
        "تم إرسال تقرير القياسات وروابط الملف الطبي إلى بريدك الإلكتروني.",
      endButton: "إنهاء الجلسة",
      confirmEnd: "هل أنت متأكد من إنهاء هذه الجلسة؟",
      sessionSummary: "ملخص الجلسة",
    },

    // Chat Manager
    chatManager: {
      title: "تحدث مع مدير الحالة",
      instruction:
        "امسح رمز الاستجابة السريعة وسنقوم بتوصيلك بأحد مديري الحالة لدينا",
    },

    // Language Toggle
    language: {
      english: "English",
      arabic: "العربية",
      selectLanguage: "اختر اللغة",
    },
  },
};

export const LocalizationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'en'
    return localStorage.getItem("app-language") || "en";
  });

  const [isRTL, setIsRTL] = useState(language === "ar");

  // Update RTL state when language changes
  useEffect(() => {
    setIsRTL(language === "ar");
    localStorage.setItem("app-language", language);

    // Update document direction
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, params = {}) => {
    const keys = key.split(".");
    let translation = translations[language];

    for (const k of keys) {
      translation = translation?.[k];
    }

    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    // Replace parameters in translation
    return translation.replace(
      /\{(\w+)\}/g,
      (match, param) => params[param] || match
    );
  };

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "en" ? "ar" : "en"));
  };

  const value = {
    language,
    isRTL,
    t,
    toggleLanguage,
    setLanguage,
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};
